// Security camera detection + hacking. Cameras pan back-and-forth
// across a fixed base direction; if their cone catches the player and
// they're not currently hacked, the alarm fires.

import { angleDiff, dist, raycastHit } from '../lib/geometry.js';
import type { Camera, World } from '../world/types.js';

/** Hack range from the player to the camera (px). */
export const HACK_RANGE = 70;
/** Hack disable duration (s). */
export const HACK_DURATION = 6;

/** Effective facing direction at the current pan offset. */
export function cameraDir(c: Camera): number {
  return c.baseDir + Math.sin(c.panT * c.panSpeed) * c.panAmplitude;
}

/**
 * Tick all cameras forward, decay any active hack timer, and report
 * whether at least one camera caught the player on this frame. Caller
 * (main.ts) decides what to do with that signal — we keep this module
 * a pure detector.
 */
export function updateCameras(world: World, dt: number): boolean {
  let alarmed = false;
  for (const c of world.cameras) {
    c.panT += dt;
    if (c.hackT > 0) {
      c.hackT = Math.max(0, c.hackT - dt);
      continue;
    }
    if (cameraSeesPlayer(world, c)) alarmed = true;
  }
  return alarmed;
}

export function cameraSeesPlayer(world: World, c: Camera): boolean {
  const { player } = world;
  if (!player.hp) return false;
  // The box is full cover when stationary, just like for guards. A
  // moving box at point-blank, however, gets noticed (handled below).
  const speed = Math.hypot(player.vx, player.vy);
  if (player.inBox && speed < 8) return false;

  const dx = player.x - c.x;
  const dy = player.y - c.y;
  const d = Math.hypot(dx, dy);
  if (d > c.vision) return false;

  const ang = Math.atan2(dy, dx);
  if (Math.abs(angleDiff(ang, cameraDir(c))) > c.fov / 2) return false;

  if (raycastHit(world, c.x, c.y, player.x, player.y)) return false;
  return true;
}

/** Nearest active camera in line of sight, or null. */
export function findHackTarget(world: World): Camera | null {
  let nearest: Camera | null = null;
  let nearestD = Infinity;
  for (const c of world.cameras) {
    if (c.hackT > 0) continue;
    const d = dist(world.player, c);
    if (d > HACK_RANGE) continue;
    if (raycastHit(world, world.player.x, world.player.y, c.x, c.y)) continue;
    if (d < nearestD) {
      nearestD = d;
      nearest = c;
    }
  }
  return nearest;
}

export function hackCamera(c: Camera): void {
  c.hackT = HACK_DURATION;
}
